import React, { memo, useMemo, useRef } from 'react';
import { Whisper } from 'rsuite';
import { InputAddon, InputGroup } from '../../../../../../components';
import { IPAMaths } from '../../../../../../lib';
import { useEditedPriceManagerStore } from '../../../../../../store/priceManagerStore';
import { VariationBoxIndicator } from '../../../../Negociacoes/pages/NegociacaoFornecedor/components';
import { MarginInput } from '../../MarginInput';
import { MarginLimitInfo } from '../../MarginLimitInfo';
import { isMarginOutsideLimit, resolveMarginLimitSkin } from '../../limitConformity';
import { BaseCell } from '../BaseCell';
import { BaseCellWithUpdateCampoProps } from '../BaseCell/BaseCell';
import { BrokenLimitFilterValue, getBrokenLimitWarning } from '../../../utils/brokenLimitWarningUtils';
import { BrokenLimitWarningIcon } from '../../BrokenLimitWarningIcon';

type MargemCellProps = BaseCellWithUpdateCampoProps & {
  handleRestoreRow: NonNullable<BaseCellWithUpdateCampoProps['handleRestoreRow']>;
  brokenLimitFilter?: BrokenLimitFilterValue;
};

const handleEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, className: string) => {
  if (e.key === 'Enter') {
    e.preventDefault();

    const currentInput = e.currentTarget;

    const nodeList = document.querySelectorAll(`.${className}`);
    const currentIndex = Array.from(nodeList).indexOf(currentInput);
    const nextIndex = currentIndex + 1;

    const nextInput = nextIndex < nodeList.length ? (nodeList[nextIndex] as HTMLInputElement) : null;

    currentInput.blur();

    if (nextInput) {
      setTimeout(() => {
        nextInput.focus();
        setTimeout(() => {
          nextInput.select();
        }, 100);
      }, 50);
    }
  }
};

const MargemCell = ({ rowData, index, dataKey, handleUpdateCampo, handleRestoreRow, handleBlur, brokenLimitFilter = null, ...props }: MargemCellProps) => {
  const { novaMargem, novaCompetitividade, originalMargin } = rowData;
  const isPendingEditedPrice = useEditedPriceManagerStore(state => Boolean(state.pendingEditedPriceRows[rowData.productsToBePricedId]));
  const focusRowDataRef = useRef<MargemCellProps['rowData'] | null>(null);
  const hasEditedSinceFocusRef = useRef(false);

  const localBroken = isMarginOutsideLimit(rowData);
  const skin = useMemo(() => resolveMarginLimitSkin(rowData, brokenLimitFilter), [rowData, brokenLimitFilter]);

  const brokenLimitWarning = useMemo(
    () => getBrokenLimitWarning({ rowData, localBroken, type: 'MARGIN', brokenLimitFilter }),
    [rowData, localBroken, brokenLimitFilter],
  );

  const marginVariation = useMemo(() => IPAMaths.variation.margem(novaMargem, originalMargin), [novaMargem, originalMargin]);

  return (
    <BaseCell rowData={rowData} {...props}>
      <BaseCell.MainContainer>
        <InputGroup>
          <MarginInput
            className="nova-margem"
            skin={skin}
            name={dataKey}
            decimalSeparator=","
            thousandSeparator="."
            precision="1"
            allowEmpty
            allowNegative
            cost={rowData.cost}
            expensesPercent={rowData.expensesPercent}
            salesTaxPercentage={rowData.salesTaxPercentage}
            onFocus={() => {
              hasEditedSinceFocusRef.current = false;
              focusRowDataRef.current = rowData;
            }}
            onFallback={() => {
              hasEditedSinceFocusRef.current = false;
              const focusRowData = focusRowDataRef.current;
              if (!focusRowData) return;

              handleRestoreRow(focusRowData, index);
            }}
            onChangeEvent={(_m, _v, value) => {
              hasEditedSinceFocusRef.current = true;
              handleUpdateCampo(dataKey, value, rowData, index);
            }}
            value={novaMargem || 0}
            disabled={isPendingEditedPrice}
            onBlur={e => {
              const wasEdited = hasEditedSinceFocusRef.current;
              hasEditedSinceFocusRef.current = false;

              if (wasEdited) handleBlur(rowData, index);
              props.onBlur?.(e);
            }}
            onKeyDown={e => handleEnterKeyPress(e, 'nova-margem')}
          />
          <Whisper
            trigger="click"
            placement="bottomEnd"
            speaker={
              // @ts-ignore - Props injected by Whisper
              <MarginLimitInfo content={rowData} margin={novaMargem} cpi={novaCompetitividade} />
            }
          >
            <InputAddon>%</InputAddon>
          </Whisper>
        </InputGroup>
      </BaseCell.MainContainer>

      <BaseCell.SubContainer>
        {!!originalMargin && !!novaMargem ? <VariationBoxIndicator className="box-indicator" value={marginVariation} /> : <>--</>}
        <BrokenLimitWarningIcon warning={brokenLimitWarning} />
      </BaseCell.SubContainer>
    </BaseCell>
  );
};

export default memo(MargemCell);
